/**
 * Field Type Detection Utilities
 *
 * Centralized logic for detecting field types in entity detail dialogs.
 * Uses EntityRef and isARef for all attribute identification (no hardcoded ids/displayNames).
 */

import { isEmailTemplate, isWorkflow, isWorkflowActivity, isContent, isSet } from './entityTypeDetection';
import { debugService } from '../services/DebugService';
import { createEntityRef, getEntityValueFromARef } from '../types';
import { isARef, isReferenceObject } from './entityPropertyUtils';

/**
 * Check if a string is valid JSON (object or array, not primitives)
 */
function isJsonString(value: unknown): boolean {
    if (typeof value !== 'string' || (value as string).trim() === '') {
        return false;
    }
    try {
        const parsed = JSON.parse(value as string);
        return typeof parsed === 'object' && parsed !== null;
    } catch {
        return false;
    }
}

export interface FieldTypeInfo {
    isEmailField: boolean;
    isIdField: boolean;
    isSequenceField: boolean;
    isIdOrSequenceField: boolean;
    isWorkflowConfig: boolean;
    isScriptField: boolean;
    isSqlField: boolean;
    isJsonField: boolean;
    isJsonStringField: boolean;
    isJsonObject: boolean;
    isOperationField: boolean;
    isOperationsField: boolean;
    isContentBodyMarkdown: boolean;
    isContentBodyHtml: boolean;
    isBodyField: boolean;
    isFilterField: boolean;
    isClickableFilter: boolean;
}

/**
 * Detect field type information for a given field.
 * All attribute identification uses EntityRef + isARef (no raw id/displayName literals).
 *
 * @param key - Field name/key (attribute id, e.g. 'Body' or '$.Body')
 * @param value - Field value
 * @param entityType - Entity type name
 * @param fullEntity - Full entity object (for context-dependent checks)
 * @param section - Section where field appears ('basic' | 'system')
 */
export function detectFieldType(
    key: string,
    value: unknown,
    entityType: string,
    fullEntity?: unknown,
    section: 'basic' | 'system' = 'basic'
): FieldTypeInfo {
    const attrRef = createEntityRef(key);
    const valueType = typeof value;
    const isString = valueType === 'string';
    const isObject = valueType === 'object' && value !== null && !Array.isArray(value);

    const isEmailTemplateEntity = isEmailTemplate(entityType);
    const isContentEntity = isContent(entityType);
    const isWorkflowEntity = isWorkflow(entityType);
    const isWorkflowActivityEntity = isWorkflowActivity(entityType);

    let contentType: string | null = null;
    if (isContentEntity && fullEntity && typeof fullEntity === 'object') {
        const e = fullEntity as Record<string, unknown>;
        contentType = String(getEntityValueFromARef('ContentType', e) ?? '').toLowerCase();
    } else if (isEmailTemplateEntity && fullEntity && typeof fullEntity === 'object') {
        const e = fullEntity as Record<string, unknown>;
        const ct = getEntityValueFromARef('ContentType', e);
        contentType = ct && typeof ct === 'string' ? (ct as string).toLowerCase() : 'text/html';
    }

    const isEmailField =
        (isARef(attrRef, 'Email') || isARef(attrRef, 'PersonalEmail') || isARef(attrRef, 'UserPrincipalName')) &&
        isString &&
        (value as string).trim() !== '';

    const isIdField = isARef(attrRef, 'Id') && isString && (value as string).trim() !== '';

    const isSequenceField =
        isARef(attrRef, 'Sequence') && (isString || valueType === 'number');

    const isIdOrSequenceField = isIdField || isSequenceField;

    const isWorkflowConfig =
        (isARef(attrRef, 'Config') || isARef(attrRef, 'Settings')) &&
        (isWorkflowEntity || isWorkflowActivityEntity) &&
        (isString || isObject);

    const isScriptField = isARef(attrRef, 'Script') && isString;
    const isScriptFieldInSystem = section === 'system' && isScriptField && isWorkflowActivityEntity;

    const isSqlField =
        (isARef(attrRef, 'SqlStatement') || isARef(attrRef, 'SqlStatementExplicit') || isARef(attrRef, '_query')) &&
        isString;

    const isOperationField = isARef(attrRef, 'Operation');
    const isOperationsField = isARef(attrRef, 'Operations');
    const isModificationField = isARef(attrRef, 'modification');
    const isDebugField = debugService.isEnabled() && isARef(attrRef, '$debug');

    const jsonStringCheck = isString ? isJsonString(value) : false;
    const isObjectOrArray = isObject || (valueType === 'object' && value !== null && Array.isArray(value));
    const isKnownJsonKey = (isOperationField || isOperationsField || isModificationField || isDebugField) && isObjectOrArray;
    const isPlainJsonLike = isObjectOrArray && (Array.isArray(value) || !isReferenceObject(value));
    const isJsonObject = isKnownJsonKey || isPlainJsonLike;
    const isJsonStringField = (isOperationField || isOperationsField || isModificationField || jsonStringCheck) && isString;
    const isJsonField = isJsonStringField || isJsonObject;

    const isContentBodyMarkdown =
        isContentEntity &&
        isARef(attrRef, 'Body') &&
        isString &&
        (contentType === 'text/markdown' || contentType === 'markdown');

    const isContentBodyHtml =
        (isContentEntity || isEmailTemplateEntity) &&
        isARef(attrRef, 'Body') &&
        isString &&
        (contentType === 'text/html' || contentType === 'html');

    const isBodyField =
        isARef(attrRef, 'Body') && isEmailTemplateEntity && isString && !isContentBodyHtml;

    const isSetEntity = isSet(entityType);
    const isFilterField = isARef(attrRef, 'Filter');
    const hasFilterScope = Boolean(
        fullEntity && typeof fullEntity === 'object' && getEntityValueFromARef('FilterScope', fullEntity as Record<string, unknown>) != null
    );
    const isClickableFilter: boolean =
        isFilterField &&
        isSetEntity &&
        typeof value === 'string' &&
        (value as string).trim() !== '' &&
        hasFilterScope;

    return {
        isEmailField,
        isIdField,
        isSequenceField,
        isIdOrSequenceField,
        isWorkflowConfig,
        isScriptField: section === 'system' ? isScriptFieldInSystem : isScriptField,
        isSqlField,
        isJsonField,
        isJsonStringField,
        isJsonObject,
        isOperationField,
        isOperationsField,
        isContentBodyMarkdown,
        isContentBodyHtml,
        isBodyField,
        isFilterField,
        isClickableFilter
    };
}
